import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, Upload, ExternalLink, FileText, Camera, MessageSquare, PenLine, FileDown, Check, HardHat, Printer, ChevronLeft, ChevronRight, BookUser, Cloud } from "lucide-react";
import { api, API } from "../../lib/api";

/** Modale crea/modifica consegna Installers: dati cliente, merce, allegati/disegni, note e foto. */
const emptyForm = {
  title: "", client_name: "", client_address: "", client_phone: "", client_email: "",
  client_delivery_notes: "", task_type: "delivery",
  scheduled_date: "", scheduled_end_date: "", scheduled_time: "09:00", assigned_to_ids: [], riferimento: "", notes_admin: "", goods: [],
};

const PHOTO_CAT_LABELS = {
  reportage: "Reportage",
  avanzamento: "Avanzamento",
  contestazione: "Contestazioni",
  misure: "Misure",
};

export default function InstallerTaskModal({ task, workers, defaultDate, duplicateFrom, defaultType, onClose, onSaved }) {
  const editing = !!task;
  const src = task || duplicateFrom || null;
  const [form, setForm] = useState(() => src ? {
    title: src.title, client_name: src.client_name, client_address: src.client_address,
    client_phone: src.client_phone, client_email: src.client_email,
    client_delivery_notes: src.client_delivery_notes || "",
    task_type: src.task_type || "delivery",
    scheduled_date: task ? src.scheduled_date : (defaultDate || new Date().toLocaleDateString("sv-SE")),
    scheduled_end_date: src.scheduled_end_date || "",
    scheduled_time: src.scheduled_time || "09:00",
    assigned_to_ids: (src.assigned_to_ids && src.assigned_to_ids.length ? [...src.assigned_to_ids] : (src.assigned_to ? [src.assigned_to] : [])),
    riferimento: src.riferimento || "",
    notes_admin: src.notes_admin || "",
    status: task ? (src.status || "pending") : undefined,
    goods: (src.goods || []).map((g) => task
      ? { ...g }
      : { name: g.name, qty: g.qty, marca: g.marca || "", colli: g.colli ?? "", riferimento: g.riferimento || "", loaded: false, not_found: false }),
  } : { ...emptyForm, task_type: defaultType || "delivery", scheduled_date: defaultType === "assistance" ? "" : (defaultDate || new Date().toLocaleDateString("sv-SE")) });
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null); // indice foto aperta nel visualizzatore
  const [photoCat, setPhotoCat] = useState("avanzamento"); // cartella destinazione upload admin
  const [sigDeleted, setSigDeleted] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [ficResults, setFicResults] = useState([]);
  const ficEnabled = useRef(true); // disattivato se FiC non è collegato
  const token = localStorage.getItem("ah_token") || "";

  useEffect(() => {
    if (!clientQuery.trim()) { setClientResults([]); setFicResults([]); return undefined; }
    const h = setTimeout(async () => {
      const q = clientQuery.trim();
      const [loc, fic] = await Promise.allSettled([
        api.get("/installers/admin/clients", { params: { q } }),
        ficEnabled.current && q.length >= 2
          ? api.get("/admin/fatture-cloud/clients", { params: { q } })
          : Promise.reject(new Error("fic-skip")),
      ]);
      setClientResults(loc.status === "fulfilled" ? loc.value.data.slice(0, 8) : []);
      if (fic.status === "fulfilled") {
        if (fic.value.data.connected === false) ficEnabled.current = false;
        setFicResults(fic.value.data.clients || []);
      } else {
        setFicResults([]);
      }
    }, 250);
    return () => clearTimeout(h);
  }, [clientQuery]);

  function pickClient(c) {
    setForm((f) => ({
      ...f,
      client_name: c.name || "",
      client_address: c.address || "",
      client_phone: c.phone || "",
      client_email: c.email || "",
      client_delivery_notes: c.delivery_notes || "",
    }));
    setClientQuery("");
    setClientResults([]);
    setFicResults([]);
  }

  function pickFicClient(c) {
    setForm((f) => ({
      ...f,
      client_name: c.name || "",
      client_address: c.address || "",
      client_phone: c.phone || "",
      client_email: c.email || "",
    }));
    setClientQuery("");
    setClientResults([]);
    setFicResults([]);
  }

  const photoUrl = useCallback((p) => `${API}${p.url.replace("/api", "")}?token=${token}`, [token]);

  const printPhotos = useCallback((list) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const escTxt = (s) => String(s || "").replace(/</g, "&lt;");
    const figs = list.map((p) => `
      <figure style="page-break-inside:avoid;margin:0 0 20px;text-align:center">
        <img src="${photoUrl(p)}" style="max-width:100%;max-height:88vh"/>
        <figcaption style="font-size:11px;color:#555;margin-top:5px">${escTxt(PHOTO_CAT_LABELS[p.category] || p.category)} · ${escTxt(p.worker_name)}${p.comment ? " — " + escTxt(p.comment) : ""}</figcaption>
      </figure>`).join("");
    w.document.write(`<html><head><title>Foto consegna — ${escTxt(task?.title)}</title></head>
<body style="font-family:Helvetica,Arial,sans-serif;margin:24px">
<div style="font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#888;margin-bottom:14px">ARCHI-HUB · Foto consegna — ${escTxt(task?.title)}</div>
${figs}
<script>window.onload = () => setTimeout(() => window.print(), 400)</` + `script></body></html>`);
    w.document.close();
  }, [photoUrl, task]);

  useEffect(() => {
    if (viewer === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setViewer(null);
      if (e.key === "ArrowRight") setViewer((v) => (v + 1) % photos.length);
      if (e.key === "ArrowLeft") setViewer((v) => (v - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer, photos.length]);

  const loadExtras = useCallback(async () => {
    if (!task) return;
    const [a, n, p] = await Promise.allSettled([
      api.get(`/installers/tasks/${task.id}/attachments`),
      api.get(`/installers/tasks/${task.id}/notes`),
      api.get("/installers/photos", { params: { task_id: task.id } }),
    ]);
    if (a.status === "fulfilled") setAttachments(a.value.data.attachments);
    if (n.status === "fulfilled") setNotes(n.value.data);
    if (p.status === "fulfilled") setPhotos(p.value.data.photos);
    if ([a, n, p].some((r) => r.status === "rejected")) {
      toast.error("Errore caricamento di allegati/note/foto della consegna");
    }
  }, [task]);

  useEffect(() => { loadExtras(); }, [loadExtras]);

  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setGood(i, k, v) {
    setForm((f) => {
      const goods = [...f.goods];
      goods[i] = { ...goods[i], [k]: v };
      return { ...f, goods };
    });
  }

  async function save(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        goods: form.goods.filter((g) => (g.name || "").trim()).map((g) => ({
          ...g,
          qty: Number(g.qty) || 1,
          colli: g.colli === "" || g.colli == null ? null : Number(g.colli),
        })),
      };
      delete payload.title; // il titolo è il nome del cliente (sincronizzato dal backend)
      const lbl = form.task_type === "assistance" ? "Assistenza" : "Consegna";
      if (editing) {
        await api.put(`/installers/tasks/${task.id}`, payload);
        toast.success(`${lbl} aggiornata`);
      } else {
        await api.post("/installers/tasks", payload);
        toast.success(`${lbl} creata`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  }

  async function uploadAttachment(file) {
    if (!file || !editing) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/installers/tasks/${task.id}/attachments`, fd);
      toast.success("Allegato caricato");
      loadExtras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload (ammessi PDF/JPEG/PNG/WEBP, max 30MB)");
    } finally {
      setUploading(false);
    }
  }

  async function uploadPhotoAdmin(file) {
    if (!file || !editing) return;
    if (file.size > 30 * 1024 * 1024) { toast.error("Immagine troppo grande (max 30MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", photoCat);
      fd.append("task_id", task.id);
      await api.post("/installers/photos", fd);
      toast.success(`Foto caricata in "${PHOTO_CAT_LABELS[photoCat] || photoCat}"`);
      loadExtras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload foto (JPEG/PNG/WEBP, max 30MB)");
    } finally {
      setUploading(false);
    }
  }

  async function deleteAttachment(a) {
    if (!window.confirm(`Eliminare "${a.filename}"?`)) return;
    try {
      await api.delete(`/installers/attachments/${a.id}`);
      loadExtras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  async function deletePhoto(p) {
    if (!p) return;
    if (!window.confirm("Eliminare questa foto? L'operaio potrà scattarla di nuovo dall'app.")) return;
    try {
      await api.delete(`/installers/photos/${p.id}`);
      toast.success("Foto eliminata");
      setViewer(null);
      setPhotos((list) => list.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione foto");
    }
  }

  async function deleteSignature() {
    if (!window.confirm("Eliminare la firma del cliente? La consegna potrà essere rifirmata dall'app dell'operaio.")) return;
    try {
      await api.delete(`/installers/tasks/${task.id}/signature`);
      toast.success("Firma eliminata: la consegna può essere rifirmata");
      setSigDeleted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione firma");
    }
  }

  const isAssist = form.task_type === "assistance";
  return (
    <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="installer-task-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-cream w-[min(96vw,860px)] max-h-[92vh] overflow-y-auto border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between z-10">
          <div className="font-display text-lg text-ink flex items-center gap-2.5">
            {editing ? (isAssist ? "Modifica assistenza" : "Modifica consegna") : (duplicateFrom ? (isAssist ? "Duplica assistenza" : "Duplica consegna") : (isAssist ? "Nuova assistenza" : "Nuova consegna"))}
            {editing && isAssist && <span className="text-[9px] tracking-wider uppercase font-bold bg-violet-50 text-violet-900 border border-violet-300 rounded px-1.5 py-0.5" data-testid="installer-modal-assist-badge">Assistenza</span>}
          </div>
          <button onClick={onClose} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="installer-modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={save} onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault(); }} className="p-6 space-y-4">
          {!editing && (
            <div className="flex items-center gap-2" data-testid="installer-form-type">
              {[["delivery", "Consegna"], ["assistance", "Assistenza"]].map(([v, l]) => (
                <button
                  key={v} type="button"
                  onClick={() => setForm((f) => ({ ...f, task_type: v, scheduled_date: v === "assistance" ? "" : (f.scheduled_date || defaultDate || new Date().toLocaleDateString("sv-SE")) }))}
                  className={`px-4 py-2 text-[11px] tracking-[0.15em] uppercase rounded-sm border font-bold transition-colors ${form.task_type === v ? (v === "assistance" ? "bg-violet-700 text-white border-violet-700" : "bg-ink text-cream border-ink") : "bg-white text-slate1 border-ink/15 hover:text-ink"}`}
                  data-testid={`installer-form-type-${v}`}
                >
                  {l}
                </button>
              ))}
              {isAssist && <span className="text-[11px] text-slate1">Non calendarizzata: sempre visibile agli operai, evidenziata entro 5 km dalle consegne</span>}
            </div>
          )}
          <div className="relative">
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Richiama cliente da rubrica o Fatture in Cloud</label>
            <input
              placeholder="Cerca un cliente già salvato (nome, indirizzo, telefono)..."
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white"
              data-testid="installer-form-client-search"
            />
            {(clientResults.length > 0 || ficResults.length > 0) && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-ink/15 rounded-sm shadow-xl max-h-72 overflow-y-auto" data-testid="installer-client-results">
                {clientResults.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[9px] tracking-[0.18em] uppercase text-slate1 bg-cream2/70 flex items-center gap-1.5 sticky top-0"><BookUser size={10} /> Rubrica</div>
                    {clientResults.map((c) => (
                      <button
                        key={c.id} type="button" onClick={() => pickClient(c)}
                        className="w-full text-left px-3 py-2.5 hover:bg-cream2 border-b border-ink/5 last:border-0"
                        data-testid={`installer-client-pick-${c.id}`}
                      >
                        <div className="text-sm font-medium text-ink">{c.name}</div>
                        <div className="text-[11px] text-slate1 truncate">{[c.address, c.phone].filter(Boolean).join(" · ")}</div>
                      </button>
                    ))}
                  </>
                )}
                {ficResults.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[9px] tracking-[0.18em] uppercase text-sky-800 bg-sky-50 flex items-center gap-1.5" data-testid="installer-fic-section"><Cloud size={10} /> Fatture in Cloud</div>
                    {ficResults.map((c) => (
                      <button
                        key={`fic-${c.fic_id}`} type="button" onClick={() => pickFicClient(c)}
                        className="w-full text-left px-3 py-2.5 hover:bg-sky-50/70 border-b border-ink/5 last:border-0"
                        data-testid={`installer-fic-pick-${c.fic_id}`}
                      >
                        <div className="text-sm font-medium text-ink">{c.name}</div>
                        <div className="text-[11px] text-slate1 truncate">{[c.address, c.phone, c.vat_number ? `P.IVA ${c.vat_number}` : ""].filter(Boolean).join(" · ")}</div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input required placeholder={isAssist ? "Nome cliente *" : "Nome cliente * (è il titolo della consegna)"} value={form.client_name} onChange={(e) => setF("client_name", e.target.value)} className="border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-client-name" />
            <input placeholder="Indirizzo" value={form.client_address} onChange={(e) => setF("client_address", e.target.value)} className="border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-address" />
            <input placeholder="Telefono cliente" value={form.client_phone} onChange={(e) => setF("client_phone", e.target.value)} className="border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-phone" />
            <input placeholder="Email cliente" value={form.client_email} onChange={(e) => setF("client_email", e.target.value)} className="border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-email" />
          </div>
          <textarea
            placeholder={isAssist ? "Note del cliente (piano, ascensore, accessi, orari preferiti...)" : "Note di consegna del cliente (piano, ascensore, accessi, orari preferiti...)"}
            value={form.client_delivery_notes}
            onChange={(e) => setF("client_delivery_notes", e.target.value)}
            rows={2}
            className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm"
            data-testid="installer-form-delivery-notes"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">{isAssist ? "Data (facoltativa)" : "Data *"}</label>
              <input required={!isAssist} type="date" value={form.scheduled_date} onChange={(e) => setF("scheduled_date", e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-date" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Ora</label>
              <input type="time" value={form.scheduled_time} onChange={(e) => setF("scheduled_time", e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-time" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Riferimento</label>
              <input placeholder="Es. ordine 2026/145" value={form.riferimento} onChange={(e) => setF("riferimento", e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-riferimento" />
            </div>
          </div>
          <div data-testid="installer-form-enddate-wrap">
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Fine lavoro (facoltativa, per lavori su più giorni)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input type="date" min={form.scheduled_date || undefined} value={form.scheduled_end_date} onChange={(e) => setF("scheduled_end_date", e.target.value)} className="w-full md:w-56 border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="installer-form-enddate" />
              {form.scheduled_end_date && form.scheduled_end_date > form.scheduled_date
                ? <span className="text-[11px] text-sky-800 inline-flex items-center gap-1">Comparirà in ogni giornata dal {new Date(`${form.scheduled_date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })} al {new Date(`${form.scheduled_end_date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</span>
                : <span className="text-[11px] text-slate1">Lascia vuoto per un lavoro di un solo giorno</span>}
            </div>
          </div>
          {editing && (
            <div className="flex items-center gap-3 flex-wrap bg-white border border-ink/10 rounded-sm px-3 py-2.5">
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Stato</label>
              <select
                value={form.status}
                onChange={(e) => setF("status", e.target.value)}
                className="border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm"
                data-testid="installer-form-status"
              >
                <option value="pending">Da fare</option>
                <option value="in_progress">In corso</option>
                <option value="completed">Completata</option>
              </select>
              {task.status === "completed" && form.status !== "completed" && (
                <span className="text-[11px] text-amber-800">Verrà rimessa tra quelle da fare: riprogramma data/operai qui sopra se serve</span>
              )}
            </div>
          )}

          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Operai assegnati{form.assigned_to_ids.length > 0 ? ` (${form.assigned_to_ids.length})` : ""}</label>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="installer-form-workers">
              {workers.length === 0 && <span className="text-xs text-slate-400">Nessun operaio disponibile</span>}
              {workers.map((w) => {
                const on = form.assigned_to_ids.includes(w.id);
                return (
                  <button
                    key={w.id} type="button"
                    onClick={() => setF("assigned_to_ids", on ? form.assigned_to_ids.filter((i) => i !== w.id) : [...form.assigned_to_ids, w.id])}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border transition-colors ${on ? "bg-ink text-cream border-ink" : "bg-white text-slate-600 border-ink/15 hover:border-ink/40"}`}
                    data-testid={`installer-form-worker-${w.id}`}
                  >
                    {on ? <Check size={12} /> : <HardHat size={12} />} {w.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Merce */}
          <div className="bg-white border border-ink/10 rounded-sm p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">{isAssist ? "Merce / materiale necessario" : "Merce da consegnare"}</div>
              <button type="button" onClick={() => setF("goods", [...form.goods, { name: "", qty: 1, marca: "", colli: "", riferimento: form.riferimento, loaded: false, not_found: false }])} className="inline-flex items-center gap-1 text-[11px] tracking-wider uppercase text-ink bg-cream2 px-2.5 py-1.5 rounded-sm" data-testid="installer-form-add-good">
                <Plus size={12} /> Aggiungi
              </button>
            </div>
            {form.goods.length === 0 && <div className="text-xs text-slate-400">Nessuna merce inserita</div>}
            {form.goods.length > 0 && (
              <div className="hidden md:grid grid-cols-[1fr_120px_56px_64px_130px_auto] gap-2 text-[9px] tracking-[0.15em] uppercase text-slate-400 px-0.5">
                <span>Descrizione</span><span>Marca</span><span>Qtà</span><span>Colli</span><span>Riferimento</span><span></span>
              </div>
            )}
            {form.goods.map((g, i) => {
              const refDiff = (g.riferimento || "") !== (form.riferimento || "");
              return (
                <div key={g.id || i} className="grid grid-cols-2 md:grid-cols-[1fr_120px_56px_64px_130px_auto] items-center gap-2" data-testid={`installer-form-good-${i}`}>
                  <input placeholder="Descrizione articolo" value={g.name} onChange={(e) => setGood(i, "name", e.target.value)} className="col-span-2 md:col-span-1 border border-ink/15 px-3 py-2 rounded-sm bg-cream text-sm" data-testid={`installer-form-good-name-${i}`} />
                  <input placeholder="Marca" value={g.marca || ""} onChange={(e) => setGood(i, "marca", e.target.value)} className="border border-ink/15 px-2 py-2 rounded-sm bg-cream text-sm" data-testid={`installer-form-good-marca-${i}`} />
                  <input type="number" min={1} value={g.qty} onChange={(e) => setGood(i, "qty", e.target.value)} className="border border-ink/15 px-2 py-2 rounded-sm bg-cream text-sm text-center" title="Quantità" data-testid={`installer-form-good-qty-${i}`} />
                  <input type="number" min={0} placeholder="—" value={g.colli ?? ""} onChange={(e) => setGood(i, "colli", e.target.value)} className="border border-ink/15 px-2 py-2 rounded-sm bg-cream text-sm text-center" title="Numero colli" data-testid={`installer-form-good-colli-${i}`} />
                  <input placeholder="Riferimento" value={g.riferimento || ""} onChange={(e) => setGood(i, "riferimento", e.target.value)} className={`border px-2 py-2 rounded-sm text-sm ${refDiff ? "border-rose-400 bg-rose-50 text-rose-700 font-semibold" : "border-ink/15 bg-cream"}`} title={refDiff ? "Riferimento diverso da quello della consegna" : "Riferimento articolo"} data-testid={`installer-form-good-rif-${i}`} />
                  <div className="flex items-center gap-1.5">
                    {g.not_found && <span className="text-[9px] tracking-wider uppercase font-bold text-rose-700 bg-rose-50 border border-rose-300 rounded px-1.5 py-0.5" data-testid={`installer-form-good-notfound-${i}`}>Non trovato</span>}
                    {g.partial && <span className="text-[9px] tracking-wider uppercase font-bold text-orange-700 bg-orange-50 border border-orange-300 rounded px-1.5 py-0.5" title={g.partial_note || ""} data-testid={`installer-form-good-partial-${i}`}>Parziale{g.partial_note ? `: ${g.partial_note}` : ""}</span>}
                    {g.loaded && <span className="text-[9px] tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Caricata</span>}
                    <button type="button" onClick={() => setF("goods", form.goods.filter((_, j) => j !== i))} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm" data-testid={`installer-form-good-del-${i}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>

          <textarea placeholder="Note per l'operaio (notes_admin)" value={form.notes_admin} onChange={(e) => setF("notes_admin", e.target.value)} rows={2} className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="installer-form-notes-admin" />

          <button disabled={saving} className="w-full bg-ink text-cream py-3.5 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-60 flex items-center justify-center gap-2" data-testid="installer-form-save">
            {saving && <Loader2 size={14} className="animate-spin" />} {editing ? "Salva modifiche" : (isAssist ? "Crea assistenza" : "Crea consegna")}
          </button>
        </form>

        {editing && (
          <div className="px-6 pb-6 space-y-5">
            {/* Firma di consegna */}
            {task.signed && !sigDeleted && (
              <div className="bg-white border border-emerald-200 rounded-sm p-4" data-testid="installer-signature-section">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-emerald-700 inline-flex items-center gap-1.5 font-bold"><PenLine size={12} /> Consegna firmata dal cliente</div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`${API}${task.proof_pdf_url.replace("/api", "")}?token=${token}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-[10px] tracking-wider uppercase rounded-sm font-bold"
                      data-testid="installer-proof-pdf-btn"
                    >
                      <FileDown size={12} /> Prova di consegna (PDF)
                    </a>
                    <button
                      type="button"
                      onClick={deleteSignature}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] tracking-wider uppercase rounded-sm font-bold"
                      data-testid="installer-signature-delete"
                    >
                      <Trash2 size={12} /> Elimina firma
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <img src={`${API}${task.signature_url.replace("/api", "")}?token=${token}`} alt="Firma cliente" className="h-20 bg-cream2 border border-ink/10 rounded-sm px-3" data-testid="installer-signature-img" />
                  <div className="text-sm text-slate-700">
                    <div><strong>{task.signer_name}</strong></div>
                    <div className="text-xs text-slate1">{task.signed_at ? new Date(task.signed_at).toLocaleString("it-IT") : ""}</div>
                  </div>
                </div>
              </div>
            )}
            {/* Allegati / Disegni */}
            <div className="bg-white border border-ink/10 rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 inline-flex items-center gap-1.5"><FileText size={12} /> Allegati / Disegni ({attachments.length})</div>
                <label className={`inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-2 rounded-sm cursor-pointer ${uploading ? "bg-slate-100 text-slate-400" : "bg-ink text-cream"}`}>
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Carica
                  <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => { uploadAttachment(e.target.files?.[0]); e.target.value = ""; }} data-testid="installer-attachment-upload" />
                </label>
              </div>
              {attachments.length === 0 ? (
                <div className="text-xs text-slate-400">Nessun allegato: carica PDF o immagini per l&apos;operaio (max 30MB).</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {attachments.map((a) => (
                    <div key={a.id} className="border border-ink/10 rounded-sm overflow-hidden" data-testid={`installer-attachment-${a.id}`}>
                      <img src={`${API}${a.thumbnail_url.replace("/api", "")}?token=${token}`} alt={a.filename} className="w-full h-28 object-cover bg-cream2" loading="lazy" data-testid={`installer-attachment-thumb-${a.id}`} />
                      <div className="p-2">
                        <div className="text-[11px] text-ink truncate" title={a.filename}>{a.filename}</div>
                        <div className="flex items-center justify-between mt-1">
                          <a href={`${API}${a.url.replace("/api", "")}?token=${token}`} target="_blank" rel="noreferrer" className="text-[10px] text-blue-700 inline-flex items-center gap-1" data-testid={`installer-attachment-open-${a.id}`}><ExternalLink size={10} /> Apri</a>
                          <button onClick={() => deleteAttachment(a)} className="text-rose-600 p-1 hover:bg-rose-50 rounded-sm" data-testid={`installer-attachment-del-${a.id}`}><Trash2 size={12} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Note operai */}
            <div className="bg-white border border-ink/10 rounded-sm p-4">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 inline-flex items-center gap-1.5 mb-2"><MessageSquare size={12} /> Note dal cantiere ({notes.length})</div>
              {notes.length === 0 ? <div className="text-xs text-slate-400">Nessuna nota</div> : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <div key={n.id} className="text-sm text-slate-700 bg-cream2/60 rounded-sm px-3 py-2" data-testid={`installer-note-${n.id}`}>
                      <span className="font-semibold text-ink">{n.user_name}</span> · <span className="text-[11px] text-slate1">{new Date(n.created_at).toLocaleString("it-IT")}</span>
                      <div>{n.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Foto */}
            <div className="bg-white border border-ink/10 rounded-sm p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 inline-flex items-center gap-1.5"><Camera size={12} /> Foto dal cantiere ({photos.length})</div>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {editing && (
                    <div className="inline-flex items-center gap-1.5" data-testid="installer-photo-admin-uploader">
                      <select
                        value={photoCat}
                        onChange={(e) => setPhotoCat(e.target.value)}
                        className="border border-ink/20 rounded-sm px-2 py-1.5 text-[10px] bg-white text-ink"
                        title="Cartella di destinazione"
                        data-testid="installer-photo-admin-cat"
                      >
                        <option value="avanzamento">Avanzamento</option>
                        <option value="contestazione">Contestazioni</option>
                        <option value="misure">Misure</option>
                        <option value="reportage">Reportage</option>
                      </select>
                      <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-ink text-cream text-[9px] tracking-wider uppercase rounded-sm font-bold cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="installer-photo-admin-upload-label">
                        {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Carica foto
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => { uploadPhotoAdmin(e.target.files?.[0]); e.target.value = ""; }} data-testid="installer-photo-admin-upload" />
                      </label>
                    </div>
                  )}
                  {photos.length > 0 && (
                    <>
                      <a
                        href={`${API}/installers/tasks/${task.id}/photos.zip?token=${localStorage.getItem("ah_token") || ""}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-ink/20 text-ink text-[9px] tracking-wider uppercase rounded-sm font-bold"
                        data-testid="installer-photos-zip"
                      >
                        <FileDown size={11} /> Scarica ZIP
                      </a>
                      <button
                        type="button"
                        onClick={() => printPhotos(photos)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-ink text-cream text-[9px] tracking-wider uppercase rounded-sm font-bold"
                        data-testid="installer-photos-print-all"
                      >
                        <Printer size={11} /> Stampa tutte
                      </button>
                    </>
                  )}
                </div>
              </div>
              {photos.length === 0 ? <div className="text-xs text-slate-400">Nessuna foto: caricale dall&apos;app operai, oppure qui sopra scegliendo la cartella.</div> : (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {photos.map((p, idx) => (
                    <div key={p.id} className="relative">
                      <button type="button" onClick={() => setViewer(idx)} className="block text-left w-full" data-testid={`installer-photo-${p.id}`}>
                        <img src={photoUrl(p)} alt={p.category} title={p.comment || ""} className="w-full h-20 object-cover rounded-sm bg-cream2 hover:opacity-85 transition-opacity" loading="lazy" />
                        <div className="text-[9px] tracking-wider uppercase text-slate1 mt-0.5 truncate">{PHOTO_CAT_LABELS[p.category] || p.category} · {p.worker_name}</div>
                        {p.comment && <div className="text-[9px] text-slate-500 truncate" data-testid={`installer-photo-comment-${p.id}`}>{p.comment}</div>}
                      </button>
                      <button
                        type="button"
                        onClick={() => deletePhoto(p)}
                        className="absolute top-1 right-1 bg-rose-600/90 hover:bg-rose-700 text-white p-1 rounded-sm"
                        title="Elimina foto"
                        data-testid={`installer-photo-del-${p.id}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {viewer !== null && photos[viewer] && (
        <div className="fixed inset-0 z-[9800] bg-black/95 flex flex-col" data-testid="photo-viewer" onClick={() => setViewer(null)}>
          <div className="flex items-center gap-2 px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0 text-sm">
              <span className="text-[11px] tracking-wider uppercase font-bold">{PHOTO_CAT_LABELS[photos[viewer].category] || photos[viewer].category}</span>
              <span className="text-white/60 text-xs ml-2">{photos[viewer].worker_name}</span>
              {photos[viewer].comment && <span className="text-white/75 text-xs ml-2 italic">— {photos[viewer].comment}</span>}
            </div>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <span className="text-xs text-white/50 mr-2" data-testid="photo-viewer-counter">{viewer + 1} / {photos.length}</span>
              <button onClick={() => printPhotos([photos[viewer]])} className="p-2 hover:bg-white/10 rounded-sm" title="Stampa questa foto" data-testid="photo-viewer-print">
                <Printer size={17} />
              </button>
              <button onClick={() => printPhotos(photos)} className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-white/10 rounded-sm text-[10px] tracking-wider uppercase font-bold" title="Stampa tutte le foto" data-testid="photo-viewer-print-all">
                <Printer size={13} /> Tutte
              </button>
              <a href={photoUrl(photos[viewer])} target="_blank" rel="noreferrer" className="p-2 hover:bg-white/10 rounded-sm" title="Apri originale" data-testid="photo-viewer-original">
                <ExternalLink size={17} />
              </a>
              <button onClick={() => { deletePhoto(photos[viewer]); }} className="p-2 hover:bg-rose-600/30 rounded-sm text-rose-300 hover:text-rose-200" title="Elimina questa foto" data-testid="photo-viewer-delete">
                <Trash2 size={17} />
              </button>
              <button onClick={() => setViewer(null)} className="p-2 hover:bg-white/10 rounded-sm" title="Chiudi" data-testid="photo-viewer-close">
                <X size={19} />
              </button>
            </div>
          </div>
          <div className="relative flex-1 min-h-0 flex items-center justify-center px-16 pb-6">
            {photos.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setViewer((viewer - 1 + photos.length) % photos.length); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                data-testid="photo-viewer-prev"
              ><ChevronLeft size={30} /></button>
            )}
            <img
              src={photoUrl(photos[viewer])}
              alt={photos[viewer].category}
              className="max-h-full max-w-full object-contain select-none"
              onClick={(e) => e.stopPropagation()}
              data-testid="photo-viewer-img"
            />
            {photos.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setViewer((viewer + 1) % photos.length); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                data-testid="photo-viewer-next"
              ><ChevronRight size={30} /></button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
